import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Input,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Popconfirm,
	Tooltip,
	Spin,
	Badge,
	Switch,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiEyeDuotone,
	PiHardDrivesDuotone,
	PiGlobeDuotone,
	PiHouseDuotone,
	PiWifiXDuotone,
	PiWifiHighDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import { requestGetServerList, requestDeleteServer } from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";

const { Title, Text } = Typography;
const { Option } = Select;

function ServersListPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [servers, setServers] = useState([]);
	const [pingStatus, setPingStatus] = useState({}); // Store ping status for each server
	const [pingLoading, setPingLoading] = useState({}); // Store ping loading state for each server
	const [autoPing, setAutoPing] = useState(true); // Auto ping servers when loaded

	// Initialize state from URL parameters
	const [pagination, setPagination] = useState({
		current: parseInt(searchParams.get("page")) || 1,
		pageSize: parseInt(searchParams.get("limit")) || 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: searchParams.get("keyword") || "",
		status: searchParams.get("status") || "",
		type: searchParams.get("type") || "",
		region: searchParams.get("region") || "",
		panel: searchParams.get("panel") || "",
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Servers";
		fetchServers();
	}, []);

	useEffect(() => {
		fetchServers();
	}, [pagination.current, pagination.pageSize, filters]);

	// Update URL parameters when state changes
	useEffect(() => {
		const params = new URLSearchParams();

		// Add pagination parameters
		if (pagination.current > 1) params.set("page", pagination.current.toString());
		if (pagination.pageSize !== 10) params.set("limit", pagination.pageSize.toString());

		// Add filter parameters
		if (filters.keyword) params.set("keyword", filters.keyword);
		if (filters.status) params.set("status", filters.status);
		if (filters.type) params.set("type", filters.type);
		if (filters.region) params.set("region", filters.region);
		if (filters.panel) params.set("panel", filters.panel);

		// Update URL without triggering navigation
		setSearchParams(params, { replace: true });
	}, [pagination.current, pagination.pageSize, filters, setSearchParams]);

	const fetchServers = () => {
		setLoading(true);
		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			order: "name:asc",
		};

		// Build filter string
		const filterParts = [];
		if (filters.status) {
			filterParts.push(`status:${filters.status}`);
		}
		if (filters.type) {
			filterParts.push(`type:${filters.type}`);
		}
		if (filters.region) {
			filterParts.push(`region:${filters.region}`);
		}
		if (filters.panel) {
			filterParts.push(`panel:${filters.panel}`);
		}
		if (filterParts.length > 0) {
			params.filter = filterParts.join(",");
		}

		requestGetServerList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const serversData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setServers(serversData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));

				// Auto-ping servers if enabled
				if (autoPing && serversData.length > 0) {
					setTimeout(() => {
						serversData.forEach((server) => {
							pingServer(server._id, server.ip, server.port);
						});
					}, 500); // Small delay to let the UI update first
				}
			})
			.catch((error) => {
				console.error("Error fetching servers:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch servers",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleDeleteServer = (id) => {
		requestDeleteServer(id)
			.then(() => {
				message.success("Server deleted successfully");
				fetchServers();
			})
			.catch((error) => {
				console.error("Error deleting server:", error);
				if (error.response?.data?.message) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} else {
					notification.error({
						message: "Error",
						description: "Failed to delete server. Please try again.",
					});
				}
			});
	};

	const handleTableChange = (newPagination) => {
		setPagination(newPagination);
	};

	const handleSearch = (keyword) => {
		setFilters((prev) => ({ ...prev, keyword }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleStatusFilter = (status) => {
		setFilters((prev) => ({ ...prev, status }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleTypeFilter = (type) => {
		setFilters((prev) => ({ ...prev, type }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleRegionFilter = (region) => {
		setFilters((prev) => ({ ...prev, region }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handlePanelFilter = (panel) => {
		setFilters((prev) => ({ ...prev, panel }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const getServerTypeLabel = (type) => {
		const types = {
			WEHO: "Web Hosting",
			APLA: "App Launcher",
			GAME: "Game Server",
			VPS: "VPS",
			DEDI: "Dedicated Server",
		};
		return types[type] || type;
	};

	const getPanelLabel = (panel) => {
		const panels = {
			WEBUZO: "Webuzo",
			CWP: "Control Web Panel",
			COOLIFY: "Coolify",
			CLOUDPANEL: "Cloud Panel",
			CPANEL: "cPanel",
			PLESK: "Plesk",
			DIRECTADMIN: "DirectAdmin",
		};
		return panels[panel] || panel;
	};

	// Ping function to check server reachability
	const pingServer = async (serverId, ip, port = null) => {
		setPingLoading((prev) => ({ ...prev, [serverId]: true }));

		try {
			const startTime = Date.now();

			// Try different approaches to test connectivity
			let isReachable = false;
			let responseTime = 0;
			let method = "unknown";

			// Approach 1: Try HTTP/HTTPS connection
			const testUrls = [];
			if (port) {
				testUrls.push(`http://${ip}:${port}`);
				if (port === "443" || port === 443) {
					testUrls.push(`https://${ip}`);
				}
			} else {
				testUrls.push(`https://${ip}`);
				testUrls.push(`http://${ip}`);
				testUrls.push(`http://${ip}:80`);
				testUrls.push(`https://${ip}:443`);
			}

			for (const url of testUrls) {
				try {
					const controller = new AbortController();
					const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

					await fetch(url, {
						method: "HEAD",
						mode: "no-cors", // This allows cross-origin but limits response info
						signal: controller.signal,
						cache: "no-cache",
					});

					clearTimeout(timeoutId);
					responseTime = Date.now() - startTime;
					isReachable = true;
					method = `HTTP (${url.split("://")[0].toUpperCase()})`;
					break;
				} catch (error) {
					// Continue to next URL
					continue;
				}
			}

			// Approach 2: If HTTP fails, try WebSocket connection (for servers with WebSocket support)
			if (!isReachable && port) {
				try {
					const wsUrl = `ws://${ip}:${port}`;
					const ws = new WebSocket(wsUrl);

					await new Promise((resolve, reject) => {
						const timeout = setTimeout(() => {
							ws.close();
							reject(new Error("WebSocket timeout"));
						}, 2000);

						ws.onopen = () => {
							clearTimeout(timeout);
							responseTime = Date.now() - startTime;
							isReachable = true;
							method = "WebSocket";
							ws.close();
							resolve();
						};

						ws.onerror = () => {
							clearTimeout(timeout);
							reject(new Error("WebSocket connection failed"));
						};
					});
				} catch (wsError) {
					// WebSocket failed, continue with final result
				}
			}

			// Approach 3: Image loading test (as a last resort for web servers)
			if (!isReachable) {
				try {
					const img = new Image();
					const imageUrl = port ? `http://${ip}:${port}/favicon.ico` : `http://${ip}/favicon.ico`;

					await new Promise((resolve, reject) => {
						const timeout = setTimeout(() => {
							reject(new Error("Image load timeout"));
						}, 2000);

						img.onload = () => {
							clearTimeout(timeout);
							responseTime = Date.now() - startTime;
							isReachable = true;
							method = "Image Load";
							resolve();
						};

						img.onerror = () => {
							clearTimeout(timeout);
							// Even if favicon fails to load, the server responded
							responseTime = Date.now() - startTime;
							isReachable = true;
							method = "Server Response";
							resolve();
						};

						img.src = imageUrl;
					});
				} catch (imgError) {
					// Image test failed, server is likely unreachable
					responseTime = Date.now() - startTime;
				}
			}

			setPingStatus((prev) => ({
				...prev,
				[serverId]: {
					reachable: isReachable,
					responseTime: responseTime,
					lastChecked: new Date(),
					method: method,
					error: null,
				},
			}));
		} catch (error) {
			setPingStatus((prev) => ({
				...prev,
				[serverId]: {
					reachable: false,
					responseTime: Date.now() - Date.now(),
					lastChecked: new Date(),
					method: "Failed",
					error: error.message,
				},
			}));
		} finally {
			setPingLoading((prev) => ({ ...prev, [serverId]: false }));
		}
	};

	// Ping all servers
	const pingAllServers = async () => {
		if (servers.length === 0) {
			message.warning("No servers to ping");
			return;
		}

		const promises = servers.map((server) => pingServer(server._id, server.ip, server.port));

		try {
			await Promise.allSettled(promises);
			message.success(
				`Connectivity test completed for ${servers.length} server${servers.length === 1 ? "" : "s"}`
			);
		} catch (error) {
			console.error("Error pinging servers:", error);
			notification.error({
				message: "Ping Error",
				description: "Failed to ping some servers",
			});
		}
	};

	// Clear all ping statuses
	const clearPingStatus = () => {
		setPingStatus({});
		message.info("Connectivity status cleared");
	};

	// Get ping status display
	const getPingStatusDisplay = (serverId) => {
		const status = pingStatus[serverId];
		const isLoading = pingLoading[serverId];

		if (isLoading) {
			return (
				<Tooltip title="Testing connection...">
					<Spin size="small" />
				</Tooltip>
			);
		}

		if (!status) {
			return (
				<Tooltip title="Click the ping button to test connectivity">
					<Badge status="default" text="Not tested" />
				</Tooltip>
			);
		}

		if (status.reachable) {
			return (
				<Tooltip
					title={
						<div>
							<div>✅ Server is reachable</div>
							<div>Response time: {status.responseTime}ms</div>
							<div>Method: {status.method}</div>
							<div>Last checked: {status.lastChecked.toLocaleString()}</div>
						</div>
					}>
					<Badge
						status="success"
						text={<span style={{ color: "#52c41a" }}>Online ({status.responseTime}ms)</span>}
					/>
				</Tooltip>
			);
		} else {
			return (
				<Tooltip
					title={
						<div>
							<div>❌ Server is unreachable</div>
							<div>Error: {status.error || "Connection failed"}</div>
							<div>Last checked: {status.lastChecked.toLocaleString()}</div>
						</div>
					}>
					<Badge status="error" text={<span style={{ color: "#ff4d4f" }}>Offline</span>} />
				</Tooltip>
			);
		}
	};

	const columns = [
		{
			title: "Server",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiHardDrivesDuotone size={24} style={{ marginRight: 12, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{record.hostname}</div>
					</div>
				</div>
			),
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			render: (type) => <Tag color="blue">{getServerTypeLabel(type)}</Tag>,
		},
		{
			title: "Region",
			dataIndex: "region",
			key: "region",
			render: (region) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiGlobeDuotone style={{ marginRight: 4 }} />
					{region}
				</div>
			),
		},
		{
			title: "IP Address",
			dataIndex: "ip",
			key: "ip",
			render: (ip, record) => (
				<Space size={0}>
					<Text code>{ip}</Text>
					{record.port && <Text code>:{record.port}</Text>}
				</Space>
			),
		},
		{
			title: "Panel",
			dataIndex: "panel",
			key: "panel",
			render: (panel) => <Tag color="green">{getPanelLabel(panel)}</Tag>,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => (
				<Tag color={status === "ACTIVE" ? "green" : status === "MAINTENANCE" ? "orange" : "red"}>
					{status === "ACTIVE" ? "Active" : status === "MAINTENANCE" ? "Maintenance" : "Inactive"}
				</Tag>
			),
		},
		{
			title: "Created At",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Connectivity",
			key: "ping",
			align: "left",
			render: (_, record) => (
				<div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: 8 }}>
					{getPingStatusDisplay(record._id)}
					<Tooltip title="Test Connection">
						<Button
							type="text"
							size="small"
							icon={pingLoading[record._id] ? <Spin size="small" /> : <PiWifiHighDuotone />}
							loading={pingLoading[record._id]}
							onClick={() => pingServer(record._id, record.ip, record.port)}
						/>
					</Tooltip>
				</div>
			),
		},
		{
			title: "Action",
			key: "action",
			align: "right",
			render: (_, record) => (
				<Space size="middle">
					<Tooltip title="View Server">
						<Button
							type="text"
							size="small"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/settings/servers/${record._id}`)}
						/>
					</Tooltip>
					<Tooltip title="Edit Server">
						<Button
							type="text"
							size="small"
							icon={<PiPencilDuotone />}
							onClick={() => navigate(`/settings/servers/${record._id}/edit`)}
						/>
					</Tooltip>
					<Popconfirm
						title="Delete Server"
						description="Are you sure you want to delete this server? This action cannot be undone."
						onConfirm={() => handleDeleteServer(record._id)}
						okText="Yes"
						cancelText="No"
						okType="danger">
						<Tooltip title="Delete Server">
							<Button type="text" size="small" danger icon={<PiTrashDuotone />} />
						</Tooltip>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Server Management
					</Title>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="All Servers"
					extra={
						<Space>
							<Button
								icon={<PiWifiXDuotone />}
								onClick={clearPingStatus}
								disabled={Object.keys(pingStatus).length === 0}>
								Clear Status
							</Button>
							<Button
								icon={<PiWifiHighDuotone />}
								onClick={pingAllServers}
								loading={Object.values(pingLoading).some((loading) => loading)}
								disabled={servers.length === 0}>
								Test All ({servers.length})
							</Button>
							<Button
								type="primary"
								icon={<PiPlusDuotone />}
								onClick={() => navigate("/settings/servers/add")}>
								Add Server
							</Button>
						</Space>
					}>
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={8}>
							<SearchInput
								placeholder="Search servers..."
								onSearch={handleSearch}
								style={{ width: "100%" }}
							/>
						</Col>
						<Col span={3}>
							<Select
								placeholder="Status"
								onChange={handleStatusFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="ACTIVE">Active</Option>
								<Option value="MAINTENANCE">Maintenance</Option>
								<Option value="INACTIVE">Inactive</Option>
							</Select>
						</Col>
						<Col span={3}>
							<Select
								placeholder="Type"
								onChange={handleTypeFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="WEHO">Web Hosting</Option>
								<Option value="APLA">App Launcher</Option>
								<Option value="GAME">Game Server</Option>
								<Option value="VPS">VPS</Option>
								<Option value="DEDI">Dedicated</Option>
							</Select>
						</Col>
						<Col span={3}>
							<Select
								placeholder="Panel"
								onChange={handlePanelFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="WEBUZO">Webuzo</Option>
								<Option value="CWP">Control Web Panel</Option>
								<Option value="COOLIFY">Coolify</Option>
								<Option value="CLOUDPANEL">Cloud Panel</Option>
								<Option value="CPANEL">cPanel</Option>
								<Option value="PLESK">Plesk</Option>
								<Option value="DIRECTADMIN">DirectAdmin</Option>
							</Select>
						</Col>
						<Col span={7} style={{ display: "flex", alignItems: "center", gap: 8 }}>
							<Switch checked={autoPing} onChange={setAutoPing} size="small" />
							<Text type="secondary" style={{ fontSize: "12px" }}>
								Auto-ping servers on load
							</Text>
						</Col>
					</Row>
					<Table
						columns={columns}
						dataSource={servers}
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} servers`,
						}}
						onChange={handleTableChange}
						rowKey="_id"
					/>
				</Card>
			</div>
		</div>
	);
}

export default ServersListPage;
